import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  ModelBase, 
  PlayerObject,
  UpsertableBase,
} from "@models";
import { 
  DandDFeatureSource, 
} from "@dndModels";
import { DATA_TYPE_INFO, FEATURE_SOURCE_TYPE_OPTIONS } from "@dndModels/Constants";

import CommonUtilities from "@utilities/common_utilities";
import DandDAPI from "@dndUtilities/system_api";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import ObjectIndex from "@components/Navigation/ObjectIndex";
import CopyToFeatureSource from "@dndComponents/CopyToFeatureSource";
import CopyButton from "@components/input/buttons/CopyButton";
import ToastBox from "@components/display/ToastBox";
import { DataType } from '@dndModels/Types';
import DisplayStringWithReplacementsRecursive from './display/DisplayStringWithReplacementsRecursive';

interface Props { 
  data_type: DataType;
  extra_filter?: (obj: UpsertableBase) => boolean;
  name_display?: (obj: UpsertableBase) => React.ReactNode;
  extra_by_add?: () => React.ReactNode;
  disabled?: boolean;
  constants?: any;
  show_image?: boolean;
  save_all?: boolean;
  log_it?: boolean;
  quick_fields?: string[];
}

const common_util = CommonUtilities.getInstance();
const api = DandDAPI.getInstance();

export default function FSIndex({
  data_type,
  extra_filter,
  name_display,
  constants = {},
  show_image = false,
  extra_by_add,
  save_all = false,
  log_it = false,
  quick_fields
}: Props) {
  const [processing, setProcessing] = useState(false);
  const [special_copy_type, setSpecialCopyType] = useState<DataType | "">("");
  const [toast_time, setToastTime] = useState(-1);

  const dt_info = DATA_TYPE_INFO.find(o => o.data_type === data_type);

  if (!dt_info) {
    return <>Invalid Data Type</>;
  } else {
    return (
      <Grid container spacing={0} direction="row">
        { api.user && api.user.admin &&
          <Grid size={12}>
            <SelectStringBox 
              name="Special Copy to Type"
              value={special_copy_type}
              options={FEATURE_SOURCE_TYPE_OPTIONS}
              extra_options={["None"]}
              disabled={!api.online}
              onChange={setSpecialCopyType}
            />
          </Grid>
        }
        <Grid size={12}>
          <ObjectIndex 
            data_type={data_type}
            name_display={name_display}
            log_it={log_it}
            save_all={save_all}
            extra_filter={extra_filter}
            show_image={show_image}
            constants={constants}
            extra_by_add={extra_by_add}
            extra={(obj: UpsertableBase) => {
              if (obj instanceof DandDFeatureSource) {
                return (
                  <DisplayStringWithReplacementsRecursive display_string={`{feature_source_desc.${obj.data_type}.${obj._id}}`} cfs={obj} />
                );
              } else {
                return <></>;
              }
            }}
            extra_buttons={(obj: UpsertableBase) => {
              if (obj instanceof DandDFeatureSource) {
                if (api.user && api.user.admin) {
                  return (
                    <Grid container spacing={0} direction="row">
                      <Grid size={6}>
                        <CopyToFeatureSource 
                          copyMe={obj}
                          special_copy_type={special_copy_type}
                          processing={processing || !api.online}
                          setProcessing={setProcessing}
                        />
                      </Grid>
                      <Grid size={6}>
                        <CopyButton
                          label="Copy to Clipboard"
                          onClick={() => {
                            setProcessing(true);
                            api.getFullObjectForClipboard(data_type, obj._id).then(res => {
                              // console.log(res);
                              navigator.clipboard.writeText(JSON.stringify(res));
                              setToastTime(common_util.get_time_number());
                              setProcessing(false);
                            });
                          }}
                        />
                      </Grid>
                    </Grid>
                  );
                }
              } 
              return <></>;
            }}
            quick_fields={quick_fields}
          />
        </Grid>
        <ToastBox
          openTime={ toast_time }
        >
          {dt_info.name} copied to Clipboard
        </ToastBox>
      </Grid>
    ); 
  } 
}
